import {
  TOP_FILTER_OPTIONS,
  topFilterToDefaultSubFilter,
  type TopFilter,
} from "./caseTaxonomyFilter";

/** Statutes that apply across multiple practice areas (not owned by one case type). */
export type CrossCuttingStatuteKind = "evidence-qso";

export type StatuteClassification = {
  topFilter: TopFilter;
  subFilter: string;
  /** Practice area label for cards (e.g. Civil, Criminal). */
  practiceAreaLabel: string;
  /** Subject within the practice area (e.g. Civil Procedure). */
  subjectLabel: string;
  /** Set when the statute applies across civil, criminal, family, etc. */
  crossCutting?: CrossCuttingStatuteKind;
  /** Human-readable scope for card footers, e.g. "All proceedings". */
  scopeLabel?: string;
};

/**
 * Cross-cutting Pakistani statutes — used in civil, criminal, family, and other forums.
 * When adding library classification, check this list first (QSO, Court Fees, etc.).
 */
const CROSS_CUTTING_EVIDENCE_QSO = "evidence-qso" as const;

/** Practice-area top filters where QSO is routinely cited. */
export const QSO_APPLICABLE_TOP_FILTERS: readonly TopFilter[] = [
  "Civil",
  "Criminal",
  "Family",
  "Writ",
  "Rent",
  "Revenue",
  "Banking",
  "Consumer",
  "NAB",
  "FIA",
] as const;

const EVIDENCE_QSO_SUBFILTER = "Evidence (QSO)";

/** Sub-filters when top = Other (general / cross-cutting laws). */
export const STATUTE_OTHER_SUBFILTER_OPTIONS = [
  "All General Law",
  EVIDENCE_QSO_SUBFILTER,
  "Other General Law",
] as const;

function isEvidenceQsoStatute(h: string): boolean {
  return (
    h.includes("qanun-e-shahadat") ||
    h.includes("qanun e shahadat") ||
    h.includes("qanun-i-shahadat") ||
    h.includes("qanun i shahadat") ||
    /\bqso\b/.test(h) ||
    (h.includes("shahadat") && h.includes("order"))
  );
}

function detectCrossCutting(h: string): CrossCuttingStatuteKind | null {
  if (isEvidenceQsoStatute(h)) return CROSS_CUTTING_EVIDENCE_QSO;
  return null;
}

/** Sub-filters for statutes — subject-matter terms, not case procedural stages. */
export const STATUTE_SUBFILTER_OPTIONS: Record<
  Exclude<TopFilter, "All" | "Other">,
  readonly string[]
> = {
  Civil: [
    "All Civil Law",
    "Civil Procedure",
    "Contracts",
    "Property & Transfer",
    "Specific Relief",
    "Limitation",
    EVIDENCE_QSO_SUBFILTER,
    "Other Civil Law",
  ],
  Criminal: [
    "All Criminal Law",
    "Penal Code (PPC)",
    "Criminal Procedure (CrPC)",
    "Special Criminal Laws",
    EVIDENCE_QSO_SUBFILTER,
    "Other Criminal Law",
  ],
  Family: [
    "All Family Law",
    "Muslim Family Laws",
    "Family Courts",
    "Guardianship & Minors",
    EVIDENCE_QSO_SUBFILTER,
    "Other Family Law",
  ],
  Writ: [
    "All Constitutional",
    "Constitution of Pakistan",
    "Court Rules",
    EVIDENCE_QSO_SUBFILTER,
    "Other Constitutional",
  ],
  Rent: [
    "All Rent Law",
    "Provincial Rent Acts",
    "Tenancy & Ejectment",
    EVIDENCE_QSO_SUBFILTER,
    "Other Rent Law",
  ],
  Revenue: [
    "All Revenue Law",
    "Land Revenue",
    "Land Acquisition",
    "Colonization & Settlement",
    EVIDENCE_QSO_SUBFILTER,
    "Other Revenue Law",
  ],
  Banking: [
    "All Banking Law",
    "Financial Institutions",
    "Recovery & Security",
    EVIDENCE_QSO_SUBFILTER,
    "Other Banking Law",
  ],
  Consumer: [
    "All Consumer Law",
    "Consumer Protection Act",
    EVIDENCE_QSO_SUBFILTER,
    "Other Consumer Law",
  ],
  NAB: [
    "All NAB Law",
    "NAB Ordinance",
    "Accountability Laws",
    EVIDENCE_QSO_SUBFILTER,
    "Other NAB Law",
  ],
  FIA: [
    "All FIA Law",
    "FIA Act",
    "Cybercrime / PECA",
    EVIDENCE_QSO_SUBFILTER,
    "Other FIA Law",
  ],
};

const STATUTE_SUBFILTER_CASE_TYPE: Record<
  Exclude<TopFilter, "All" | "Other">,
  Record<string, string>
> = {
  Civil: {
    "Civil Procedure": "Civil Procedure",
    Contracts: "Contracts",
    "Property & Transfer": "Property & Transfer",
    "Specific Relief": "Specific Relief",
    Limitation: "Limitation",
    "Evidence (QSO)": "Evidence (QSO)",
    "Other Civil Law": "Other Civil Law",
  },
  Criminal: {
    "Penal Code (PPC)": "Penal Code (PPC)",
    "Criminal Procedure (CrPC)": "Criminal Procedure (CrPC)",
    "Special Criminal Laws": "Special Criminal Laws",
    [EVIDENCE_QSO_SUBFILTER]: EVIDENCE_QSO_SUBFILTER,
    "Other Criminal Law": "Other Criminal Law",
  },
  Family: {
    "Muslim Family Laws": "Muslim Family Laws",
    "Family Courts": "Family Courts",
    "Guardianship & Minors": "Guardianship & Minors",
    [EVIDENCE_QSO_SUBFILTER]: EVIDENCE_QSO_SUBFILTER,
    "Other Family Law": "Other Family Law",
  },
  Writ: {
    "Constitution of Pakistan": "Constitution of Pakistan",
    "Court Rules": "Court Rules",
    [EVIDENCE_QSO_SUBFILTER]: EVIDENCE_QSO_SUBFILTER,
    "Other Constitutional": "Other Constitutional",
  },
  Rent: {
    "Provincial Rent Acts": "Provincial Rent Acts",
    "Tenancy & Ejectment": "Tenancy & Ejectment",
    [EVIDENCE_QSO_SUBFILTER]: EVIDENCE_QSO_SUBFILTER,
    "Other Rent Law": "Other Rent Law",
  },
  Revenue: {
    "Land Revenue": "Land Revenue",
    "Land Acquisition": "Land Acquisition",
    "Colonization & Settlement": "Colonization & Settlement",
    [EVIDENCE_QSO_SUBFILTER]: EVIDENCE_QSO_SUBFILTER,
    "Other Revenue Law": "Other Revenue Law",
  },
  Banking: {
    "Financial Institutions": "Financial Institutions",
    "Recovery & Security": "Recovery & Security",
    [EVIDENCE_QSO_SUBFILTER]: EVIDENCE_QSO_SUBFILTER,
    "Other Banking Law": "Other Banking Law",
  },
  Consumer: {
    "Consumer Protection Act": "Consumer Protection Act",
    [EVIDENCE_QSO_SUBFILTER]: EVIDENCE_QSO_SUBFILTER,
    "Other Consumer Law": "Other Consumer Law",
  },
  NAB: {
    "NAB Ordinance": "NAB Ordinance",
    "Accountability Laws": "Accountability Laws",
    [EVIDENCE_QSO_SUBFILTER]: EVIDENCE_QSO_SUBFILTER,
    "Other NAB Law": "Other NAB Law",
  },
  FIA: {
    "FIA Act": "FIA Act",
    "Cybercrime / PECA": "Cybercrime / PECA",
    [EVIDENCE_QSO_SUBFILTER]: EVIDENCE_QSO_SUBFILTER,
    "Other FIA Law": "Other FIA Law",
  },
};

export const STATUTE_TOP_FILTER_PRIORITY: TopFilter[] = TOP_FILTER_OPTIONS.filter(
  (t) => t !== "Other",
);

function norm(input: string | undefined): string {
  return String(input || "")
    .toLowerCase()
    .replace(/[_/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function haystack(statute: {
  title?: string;
  description?: string;
  category?: string;
  storagePath?: string;
}): string {
  return norm(
    [statute.title, statute.description, statute.category, statute.storagePath]
      .filter(Boolean)
      .join(" "),
  );
}

function legacyCategoryTopFilter(rawCategory: string | undefined): TopFilter | null {
  const c = norm(rawCategory).replace(/-/g, " ");
  if (!c) return null;
  if (
    c.includes("civil suit") ||
    c === "civil" ||
    c.includes("civils suit") ||
    c.includes("civil suits")
  ) {
    return "Civil";
  }
  if (c.includes("criminal") || c.includes("bail") || c.includes("trial")) return "Criminal";
  if (c.includes("family")) return "Family";
  if (
    c.includes("writ") ||
    c.includes("constitutional") ||
    c.includes("constitution") ||
    c.includes("write petition")
  ) {
    return "Writ";
  }
  if (c.includes("rent") || c.includes("tenancy") || c.includes("ejectment")) return "Rent";
  if (c.includes("revenue") || c.includes("land revenue") || c.includes("mutation")) return "Revenue";
  if (c.includes("banking") || c.includes("financial institution")) return "Banking";
  if (c.includes("consumer")) return "Consumer";
  if (c.includes("nab") || c.includes("accountability")) return "NAB";
  if (c.includes("fia") || c.includes("peca") || c.includes("cybercrime")) return "FIA";
  if (c === "other") return "Other";
  return null;
}

function inferTopFilter(h: string): TopFilter {
  if (
    h.includes("constitution of pakistan") ||
    h.includes("constitution of the islamic republic") ||
    /\bconstitution\b/.test(h) ||
    h.includes("article 199") ||
    h.includes("fundamental right")
  ) {
    return "Writ";
  }
  if (
    h.includes("penal code") ||
    h.includes("pakistan penal") ||
    /\bppc\b/.test(h) ||
    h.includes("criminal procedure") ||
    h.includes("code of criminal procedure") ||
    /\bcrpc\b/.test(h) ||
    /\bcr\.?p\.?c\b/.test(h) ||
    h.includes("anti terrorism") ||
    h.includes("anti-terrorism") ||
    /\bata\b/.test(h)
  ) {
    return "Criminal";
  }
  if (
    h.includes("muslim family") ||
    h.includes("family court") ||
    h.includes("dissolution of marriage") ||
    h.includes("guardian") ||
    h.includes("guardians and wards") ||
    h.includes("child marriage") ||
    h.includes("dower") ||
    h.includes("khula") ||
    h.includes("nikah")
  ) {
    return "Family";
  }
  if (
    h.includes("rent act") ||
    h.includes("tenancy") ||
    h.includes("ejectment") ||
    h.includes("sindh rent") ||
    h.includes("punjab rent")
  ) {
    return "Rent";
  }
  if (
    h.includes("land revenue") ||
    h.includes("colonization of government lands") ||
    h.includes("board of revenue") ||
    h.includes("land acquisition")
  ) {
    return "Revenue";
  }
  if (
    h.includes("financial institutions") ||
    h.includes("banking companies") ||
    h.includes("financial recovery")
  ) {
    return "Banking";
  }
  if (h.includes("consumer protection")) return "Consumer";
  if (h.includes("nab ordinance") || h.includes("accountability")) return "NAB";
  if (
    h.includes("federal investigation") ||
    h.includes("fia act") ||
    h.includes("peca") ||
    h.includes("electronic crimes") ||
    h.includes("cybercrime")
  ) {
    return "FIA";
  }
  if (
    h.includes("civil procedure") ||
    h.includes("code of civil procedure") ||
    /\bcpc\b/.test(h) ||
    h.includes("contract act") ||
    h.includes("specific relief") ||
    h.includes("limitation act") ||
    h.includes("transfer of property") ||
    h.includes("registration act") ||
    h.includes("easement") ||
    h.includes("civil suits") ||
    h.includes("civils-suits") ||
    h.includes("civils suits")
  ) {
    return "Civil";
  }
  return "Other";
}

function inferSubFilter(top: TopFilter, h: string): string {
  if (top === "All" || top === "Other") return STATUTE_SUBFILTER_OPTIONS.Civil[0];

  switch (top) {
    case "Civil":
      if (h.includes("civil procedure") || h.includes("code of civil procedure") || /\bcpc\b/.test(h))
        return "Civil Procedure";
      if (h.includes("contract act") || h.includes("contract")) return "Contracts";
      if (h.includes("specific relief")) return "Specific Relief";
      if (h.includes("limitation act") || h.includes("limitation")) return "Limitation";
      if (h.includes("transfer of property") || h.includes("property act")) return "Property & Transfer";
      return "Other Civil Law";
    case "Criminal":
      if (h.includes("penal code") || h.includes("pakistan penal") || /\bppc\b/.test(h))
        return "Penal Code (PPC)";
      if (
        h.includes("criminal procedure") ||
        h.includes("code of criminal procedure") ||
        /\bcrpc\b/.test(h) ||
        /\bcr\.?p\.?c\b/.test(h)
      ) {
        return "Criminal Procedure (CrPC)";
      }
      if (h.includes("anti terrorism") || h.includes("anti-terrorism") || /\bata\b/.test(h))
        return "Special Criminal Laws";
      return "Other Criminal Law";
    case "Family":
      if (h.includes("muslim family")) return "Muslim Family Laws";
      if (h.includes("family court")) return "Family Courts";
      if (h.includes("guardian") || h.includes("guardians and wards")) return "Guardianship & Minors";
      return "Other Family Law";
    case "Writ":
      if (h.includes("constitution")) return "Constitution of Pakistan";
      if (h.includes("court rule") || h.includes("rules of")) return "Court Rules";
      return "Other Constitutional";
    case "Rent":
      if (h.includes("ejectment") || h.includes("tenancy")) return "Tenancy & Ejectment";
      if (h.includes("rent act")) return "Provincial Rent Acts";
      return "Other Rent Law";
    case "Revenue":
      if (h.includes("land acquisition")) return "Land Acquisition";
      if (h.includes("colonization")) return "Colonization & Settlement";
      if (h.includes("land revenue") || h.includes("revenue act")) return "Land Revenue";
      return "Other Revenue Law";
    case "Banking":
      if (h.includes("financial institution")) return "Financial Institutions";
      if (h.includes("recovery") || h.includes("mortgage") || h.includes("pledge"))
        return "Recovery & Security";
      return "Other Banking Law";
    case "Consumer":
      if (h.includes("consumer protection")) return "Consumer Protection Act";
      return "Other Consumer Law";
    case "NAB":
      if (h.includes("nab ordinance")) return "NAB Ordinance";
      return "Accountability Laws";
    case "FIA":
      if (h.includes("peca") || h.includes("electronic crimes") || h.includes("cybercrime"))
        return "Cybercrime / PECA";
      if (h.includes("fia")) return "FIA Act";
      return "Other FIA Law";
    default:
      return STATUTE_SUBFILTER_OPTIONS[top][0];
  }
}

export function statuteTopFilterToDefaultSubFilter(top: TopFilter): string {
  if (top === "Other") return STATUTE_OTHER_SUBFILTER_OPTIONS[0];
  if (top === "All") return STATUTE_SUBFILTER_OPTIONS.Civil[0];
  return STATUTE_SUBFILTER_OPTIONS[top][0];
}

function matchesCrossCuttingQso(
  classification: StatuteClassification,
  topFilter: TopFilter,
  subFilter: string,
): boolean {
  if (classification.crossCutting !== CROSS_CUTTING_EVIDENCE_QSO) return false;

  if (topFilter === "All") return true;

  if (topFilter === "Other") {
    if (subFilter === STATUTE_OTHER_SUBFILTER_OPTIONS[0]) return true;
    if (subFilter === EVIDENCE_QSO_SUBFILTER) return true;
    return false;
  }

  if (!QSO_APPLICABLE_TOP_FILTERS.includes(topFilter)) return false;

  const allLabel = STATUTE_SUBFILTER_OPTIONS[topFilter][0];
  if (subFilter === allLabel) return true;
  if (subFilter === EVIDENCE_QSO_SUBFILTER) return true;

  return false;
}

export function classifyStatute(statute: {
  title?: string;
  description?: string;
  category?: string;
  storagePath?: string;
}): StatuteClassification {
  const h = haystack(statute);
  const crossCutting = detectCrossCutting(h);

  if (crossCutting === CROSS_CUTTING_EVIDENCE_QSO) {
    return {
      topFilter: "Other",
      subFilter: EVIDENCE_QSO_SUBFILTER,
      practiceAreaLabel: "General Law",
      subjectLabel: EVIDENCE_QSO_SUBFILTER,
      crossCutting: CROSS_CUTTING_EVIDENCE_QSO,
      scopeLabel: "All proceedings (civil, criminal, family & other)",
    };
  }

  const fromTitle = inferTopFilter(h);
  const fromLegacy = legacyCategoryTopFilter(statute.category);
  // Prefer title/path inference — Firestore may still carry old case-type categories.
  const topFilter =
    fromTitle !== "Other" ? fromTitle : fromLegacy ?? "Other";
  const subFilter = inferSubFilter(topFilter, h);

  return {
    topFilter,
    subFilter,
    practiceAreaLabel: topFilter === "Writ" ? "Constitutional / Writ" : topFilter,
    subjectLabel: subFilter,
  };
}

export function matchesStatuteTaxonomy(
  classification: StatuteClassification,
  topFilter: TopFilter,
  subFilter: string,
): boolean {
  if (topFilter === "All") return true;

  if (matchesCrossCuttingQso(classification, topFilter, subFilter)) return true;

  if (classification.topFilter !== topFilter) return false;
  if (topFilter === "Other") {
    if (subFilter === STATUTE_OTHER_SUBFILTER_OPTIONS[0]) return true;
    return classification.subFilter === subFilter;
  }

  const allLabel = STATUTE_SUBFILTER_OPTIONS[topFilter][0];
  if (subFilter === allLabel) return true;

  const expected = STATUTE_SUBFILTER_CASE_TYPE[topFilter][subFilter];
  if (!expected) return true;

  return classification.subFilter === expected;
}

/** Section heading on Library All view — matches top filter chip labels. */
export function topFilterSectionLabel(top: TopFilter): string {
  return top;
}

/** Card / group label — includes cross-cutting scope when relevant. */
export function statuteDisplayCategoryLabel(classification: StatuteClassification): string {
  if (classification.crossCutting === CROSS_CUTTING_EVIDENCE_QSO) {
    return `General Law · ${classification.scopeLabel ?? "All proceedings"}`;
  }
  const area =
    classification.topFilter === "Writ"
      ? "Constitutional / Writ"
      : classification.practiceAreaLabel;
  return `${area} · ${classification.subjectLabel}`;
}

export function statuteCategorySortKey(top: TopFilter): number {
  const idx = STATUTE_TOP_FILTER_PRIORITY.indexOf(top);
  if (idx >= 0) return idx;
  if (top === "Other") return 99;
  return 50;
}

/** Cross-cutting statutes (QSO) sort after area-specific procedure & substantive codes. */
const CROSS_CUTTING_DISPLAY_RANK = 9000;

function titleHaystack(statute: { title?: string; description?: string }): string {
  return norm([statute.title, statute.description].filter(Boolean).join(" "));
}

/**
 * Display order within a practice area: procedure codes first (CPC, CrPC, PPC…),
 * then other substantive laws, then cross-cutting QSO last.
 */
export function statuteDisplaySortRank(
  statute: { title?: string; description?: string; classification: StatuteClassification },
  contextTop?: TopFilter,
): number {
  const c = statute.classification;
  if (c.crossCutting === CROSS_CUTTING_EVIDENCE_QSO) return CROSS_CUTTING_DISPLAY_RANK;

  const h = titleHaystack(statute);
  const top =
    contextTop && contextTop !== "All" && contextTop !== "Other"
      ? contextTop
      : c.topFilter;

  switch (top) {
    case "Civil":
      if (
        h.includes("civil procedure") ||
        h.includes("code of civil procedure") ||
        /\bcpc\b/.test(h)
      ) {
        return 10;
      }
      if (h.includes("limitation act") || h.includes("limitation")) return 20;
      if (h.includes("contract act")) return 30;
      if (h.includes("specific relief")) return 40;
      if (h.includes("transfer of property")) return 50;
      if (h.includes("registration act")) return 55;
      if (h.includes("easement")) return 60;
      return 70;
    case "Criminal":
      if (h.includes("penal code") || h.includes("pakistan penal") || /\bppc\b/.test(h)) {
        return 10;
      }
      if (
        h.includes("criminal procedure") ||
        h.includes("code of criminal procedure") ||
        /\bcrpc\b/.test(h) ||
        /\bcr\.?p\.?c\b/.test(h)
      ) {
        return 20;
      }
      if (h.includes("anti terrorism") || h.includes("anti-terrorism") || /\bata\b/.test(h)) {
        return 30;
      }
      return 70;
    case "Family":
      if (h.includes("muslim family")) return 10;
      if (h.includes("family court")) return 20;
      if (h.includes("guardian") || h.includes("guardians and wards")) return 30;
      return 70;
    case "Writ":
      if (h.includes("constitution")) return 10;
      if (h.includes("court rule") || h.includes("rules of")) return 20;
      return 70;
    case "Rent":
      if (h.includes("rent act") || h.includes("sindh rent") || h.includes("punjab rent")) return 10;
      if (h.includes("ejectment") || h.includes("tenancy")) return 20;
      return 70;
    case "Revenue":
      if (h.includes("land revenue")) return 10;
      if (h.includes("land acquisition")) return 20;
      if (h.includes("colonization")) return 30;
      return 70;
    case "Banking":
      if (h.includes("financial institution")) return 10;
      if (h.includes("banking companies")) return 15;
      return 70;
    case "Consumer":
      if (h.includes("consumer protection")) return 10;
      return 70;
    case "NAB":
      if (h.includes("nab ordinance")) return 10;
      return 70;
    case "FIA":
      if (h.includes("fia act") || h.includes("federal investigation")) return 10;
      if (h.includes("peca") || h.includes("electronic crimes")) return 20;
      return 70;
    case "Other":
      return 50;
    default:
      return 70;
  }
}

export function sortStatutesForLibrary<
  T extends { title?: string; description?: string; classification: StatuteClassification },
>(items: T[], contextTop?: TopFilter): T[] {
  return [...items].sort((a, b) => {
    const ra = statuteDisplaySortRank(a, contextTop);
    const rb = statuteDisplaySortRank(b, contextTop);
    if (ra !== rb) return ra - rb;
    return (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" });
  });
}

/** @deprecated Use classifyStatute — kept for imports that expect a label string. */
export function statutePracticeAreaLabel(statute: {
  title?: string;
  description?: string;
  category?: string;
  storagePath?: string;
}): string {
  return classifyStatute(statute).practiceAreaLabel;
}

export { topFilterToDefaultSubFilter };
